import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import {
  CButton,
  CCol,
  CFormGroup,
  CInputGroup,
  CInputGroupAppend,
  CLabel,
  CRow,
} from "@coreui/react";
import StampSelect from "../../stamps/StampSelect";
import EstablishmentSelect from "../../establishments/EstablishmentSelect";
import DispatchPointSelect from "../../dispatch-point/DispatchPointSelect";
import ClientSelect from "../../client/ClientSelect";
import Datetime from "react-datetime";
import { useReceiptFormContext } from "../context/ReceiptFormContext";
import { FieldErrors } from "../../form/FieldErrors";
import ClientFieldErrors from "../../form/ClientFieldErrors";
import { getFieldErrors } from "../../../models/errors";

const SectionDivider: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", marginTop: "16px" }}>
    <div style={{ width: "4px", height: "18px", background: "#006657", borderRadius: "2px", marginRight: "10px", flexShrink: 0 }} />
    <i className={`fa ${icon} mr-2`} style={{ fontSize: "0.85rem", color: "var(--theme-accent)" }}></i>
    <h6 style={{ margin: 0, fontWeight: 600, color: "var(--theme-muted)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.8px" }}>
      {label}
    </h6>
    <div style={{ flex: 1, height: "1px", background: "var(--theme-divider)", marginLeft: "10px" }} />
  </div>
);

const FormLabel: React.FC<{ required?: boolean; children: React.ReactNode }> = ({ required, children }) => (
  <CLabel className="mb-0" style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}>
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

interface ReceiptFormHeaderProps {
  onAddClientClick: () => void;
}

const ReceiptFormHeader: React.FC<ReceiptFormHeaderProps> = ({
  onAddClientClick,
}) => {
  const stamps = useSelector(
    (state: RootState) => state.taxPayerParameters.data.stamps
  );
  const establishments = useSelector(
    (state: RootState) => state.taxPayerParameters.data.establishments
  );
  const dispatchPoints = useSelector(
    (state: RootState) => state.taxPayerParameters.data.dispatchPoints
  );

  const {
    stamp,
    establishment,
    dispatchPoint,
    client,
    receiptDate,
    errors,
    onStampChange,
    onEstablishmentChange,
    onDispatchPointChange,
    onClientChange,
    onReceiptDateChange,
  } = useReceiptFormContext();

  const isStampDisabled = stamps.length === 1;
  const isEstablishmentDisabled = establishments.length === 1;
  const isDispatchPointDisabled = dispatchPoints.length === 1;

  const handleEstablishmentChange = (est: typeof establishment) => {
    onEstablishmentChange(est);
    onDispatchPointChange(null);
  };

  return (
    <>
      <SectionDivider icon="fa-info-circle" label="Datos Generales" />
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Timbrado:</FormLabel>
          </CCol>
          <CCol>
            <fieldset disabled={isStampDisabled}>
              <StampSelect onChange={onStampChange} value={stamp} />
            </fieldset>
            <FieldErrors
              errors={getFieldErrors("stampId", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Establecimiento:</FormLabel>
          </CCol>
          <CCol>
            <fieldset disabled={isEstablishmentDisabled}>
              <EstablishmentSelect
                onChange={handleEstablishmentChange}
                value={establishment}
              />
            </fieldset>
            <FieldErrors
              errors={getFieldErrors("establishmentId", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Punto de Expedición:</FormLabel>
          </CCol>
          <CCol>
            <fieldset disabled={isDispatchPointDisabled}>
              <DispatchPointSelect
                key={establishment?.id ?? 0}
                onChange={onDispatchPointChange}
                value={dispatchPoint}
                establishmentId={establishment?.id}
              />
            </fieldset>
            <FieldErrors
              errors={getFieldErrors("dispatchPointId", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Cliente:</FormLabel>
          </CCol>
          <CCol>
            <CInputGroup>
              <ClientSelect editable value={client} onChange={onClientChange} />
              <CInputGroupAppend>
                <CButton
                  className="btn btn-primary"
                  style={{ zIndex: 0 }}
                  onClick={onAddClientClick}
                >
                  <i className="fa fa-user-plus"></i>
                </CButton>
              </CInputGroupAppend>
            </CInputGroup>
            <ClientFieldErrors errors={errors} />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Fecha de emisión:</FormLabel>
          </CCol>
          <CCol>
            <Datetime
              className="pl-0"
              onChange={onReceiptDateChange}
              value={receiptDate}
              locale="es/PY"
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              closeOnSelect={true}
            />
            <FieldErrors
              errors={getFieldErrors("receiptDate", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
    </>
  );
};

export default ReceiptFormHeader;
